import {
  FC, useEffect, useMemo, useRef,
} from 'react';
import {
  Box, Card, CardHeader, Skeleton, useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Chart, { useChart } from '@mgm/common/components/charts';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { useAppSelector } from '../../../../hooks/useAppStore';
import {
  getChannelData, getDailyStateSelectedLocation, getIsOrdersLoading,
} from '../../../../store/orders/selectors';
import { getCurrencyByCode } from '../../../../store/common/selectors';
import { TLocation } from '../../../../types';
import { mergeAllLocationsData } from '../../../../utils/helpers/orders.helper';
import { AnimatedDivider } from '../../../../components';

type TSalesByChannel = {
  locationsWithColors: Array<TLocation & { color: string }>;
};

const SalesByChannel: FC<TSalesByChannel> = ({ locationsWithColors }) => {
  const theme = useTheme();
  const { t } = useTranslation('orders');
  const isOrdersLoading = useAppSelector(getIsOrdersLoading);

  const channelData = useAppSelector(getChannelData);
  const dailyStateSelectedLocation = useAppSelector(getDailyStateSelectedLocation);
  const getCurrency = useAppSelector(getCurrencyByCode);
  const chartRef = useRef<any>(null); // Create a ref for the chart instance

  const currentChannelData = useMemo(() => {
    if (dailyStateSelectedLocation === 0) {
      return mergeAllLocationsData(channelData); // Merge all locations
    }
    return channelData[dailyStateSelectedLocation] || { seriesData: [], sourceNames: [] }; // Use selected location
  }, [dailyStateSelectedLocation, channelData]);

  // Map colors based on `locationsWithColors` and the location IDs in `seriesData`
  const colors = currentChannelData.seriesData.map((series) => {
    const matchingLocation = locationsWithColors.find((loc) => loc.name === series.name);
    return matchingLocation ? matchingLocation.color : theme.palette.text.disabled; // Fallback to disabled color if not found
  });

  useEffect(() => {
    if (chartRef?.current && currentChannelData?.sourceNames?.length > 0) {
      try {
        chartRef.current?.updateOptions({
          xaxis: {
            categories: currentChannelData?.sourceNames || [],
          },
        }, true, true);
      } catch (error) {
        console.error('Failed to update chart options:', error);
      }
    }
  }, [currentChannelData]);

  // Chart options for stacked bar chart
  const chartOptions = useChart({
    chart: {
      stacked: true,
      events: {
        mounted: (chart) => {
          chartRef.current = chart; // Assign chart instance to the ref
        },
      },
    },
    colors, // Use dynamically mapped colors
    stroke: {
      width: 0,
    },
    xaxis: {
      categories: currentChannelData.sourceNames,
    },
    yaxis: {
      labels: {
        formatter: (value: number) => `${value.toFixed(2)} ${getCurrency().mark}`,
      },
    },
    tooltip: {
      y: {
        formatter: (value: number) => `${value.toFixed(2)} ${getCurrency().mark}`,
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 8,
        columnWidth: '20%',
      },
    },
  });

  return (
    <Card>
      <CardHeader
        sx={{
          p: 2,
        }}
        title={t('salesByChannel')}
      />
      <AnimatedDivider loading={isOrdersLoading} />
      <Box sx={{ mx: 3 }} dir="ltr">
        {/* eslint-disable-next-line no-nested-ternary */}
        {isOrdersLoading ? (
          <Box pt={3}>
            <Skeleton variant="rectangular" height={40} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={40} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={40} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={40} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={40} />
          </Box>
        ) : currentChannelData?.sourceNames?.length ? (
          <Chart type="bar" series={currentChannelData?.seriesData} options={chartOptions} height={400} />
        ) : (
          <CustomNoRowsOverlay text={t('noDataForToday', { ns: 'common' })} />
        )}
      </Box>
    </Card>
  );
};

export { SalesByChannel };
